import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import axios from 'axios';
import { apiTokensService } from '../../../../api';
import { ApiTokenPermissions, StaffApiToken } from '../../../../api/apiTokensService';
import { useUi } from '../../../../context/ui.context';
import config from '../../../../config';
import {
  Page, Toolbar, Actions, Title, Description, Action, Filter, Empty, TableScroll,
  Table, Code, ErrorText, Usage, Dialog, DialogBody, FieldLabel, Field, Secret,
} from './styles';

const PAGE_SIZE = 20;
const statusOf = (error: unknown) => axios.isAxiosError(error) ? error.response?.status : undefined;

const TokenDialog: React.FC<{
  title: string; onClose: () => void; locked?: boolean; children: React.ReactNode;
}> = ({ title, onClose, locked, children }) => {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    dialog?.showModal();
    return () => dialog?.close();
  }, []);
  return (
    <Dialog ref={ref} aria-labelledby={titleId} onCancel={(event) => {
      event.preventDefault();
      if (!locked) onClose();
    }}>
      <DialogBody>
        <Title id={titleId}>{title}</Title>
        {children}
      </DialogBody>
    </Dialog>
  );
};

const ApiKeys: React.FC = () => {
  const [t, i18n] = useTranslation();
  const { viewAlert } = useUi();
  const [permissions, setPermissions] = useState<ApiTokenPermissions>({});
  const [accessState, setAccessState] = useState<'loading' | 'ready' | 'error' | 'denied'>('loading');
  const [accessVersion, setAccessVersion] = useState(0);
  const [listVersion, setListVersion] = useState(0);
  const [mine, setMine] = useState(false);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<StaffApiToken[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<'denied' | 'failed' | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const [deleting, setDeleting] = useState<StaffApiToken | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const canList = accessState === 'ready' && permissions['apitokens.list'] === true;
  const canCreate = accessState === 'ready' && permissions['apitokens.create'] === true;
  const canDelete = accessState === 'ready' && permissions['apitokens.destroy'] === true;

  useEffect(() => {
    const controller = new AbortController();
    setAccessState('loading');
    apiTokensService.permissions(controller.signal).then((data) => {
      if (controller.signal.aborted) return;
      if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Invalid permissions');
      setPermissions(data);
      setAccessState('ready');
    }).catch((error: unknown) => {
      if (!controller.signal.aborted) {
        setPermissions({});
        setAccessState(statusOf(error) === 403 ? 'denied' : 'error');
      }
    });
    return () => controller.abort();
  }, [accessVersion]);

  useEffect(() => {
    if (!canList) return;
    const controller = new AbortController();
    setLoading(true);
    setListError(null);
    setItems([]);
    apiTokensService.list({ mine, page, page_size: PAGE_SIZE }, controller.signal).then((data) => {
      if (controller.signal.aborted) return;
      const lastPage = Math.max(1, Math.ceil(data.count / PAGE_SIZE));
      if (page > lastPage) {
        setPage(lastPage);
        return;
      }
      setItems(data.items);
      setCount(data.count);
    }).catch((error: unknown) => {
      if (!controller.signal.aborted) setListError(statusOf(error) === 403 ? 'denied' : 'failed');
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [canList, mine, page, listVersion]);

  const createForm = useFormik({
    initialValues: { name: '' },
    validationSchema: yup.object({ name: yup.string().trim().max(255, t('API_KEYS_NAME_TOO_LONG')) }),
    onSubmit: async ({ name }, helpers) => {
      if (!canCreate) return;
      setCreateError('');
      try {
        const result = await apiTokensService.create(name);
        helpers.resetForm();
        setCreating(false);
        setListVersion((value) => value + 1);
        if (typeof result?.token === 'string' && result.token) {
          setCopied(false);
          setCopyError(false);
          setSecret(result.token);
        } else {
          // Creation may have succeeded even if the backend omitted the one-time secret.
          viewAlert({ severity: 'warning', message: t('API_KEYS_MISSING_SECRET') });
        }
      } catch (error: unknown) {
        const nameErrors = axios.isAxiosError(error) ? error.response?.data?.name : undefined;
        if (Array.isArray(nameErrors) && typeof nameErrors[0] === 'string') helpers.setFieldError('name', nameErrors[0]);
        setCreateError(t(statusOf(error) === 403 ? 'API_KEYS_NO_ACCESS' : 'API_KEYS_CREATE_FAILED'));
      }
    },
  });

  const openCreate = () => {
    createForm.resetForm();
    setCreateError('');
    setCreating(true);
  };

  const remove = async () => {
    if (!deleting || deleteBusy || !canDelete) return;
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await apiTokensService.remove(deleting.id);
      setDeleting(null);
      setListVersion((value) => value + 1);
      viewAlert({ severity: 'success', message: t('API_KEYS_DELETED') });
    } catch (error: unknown) {
      if (statusOf(error) === 404) {
        setDeleting(null);
        setListVersion((value) => value + 1);
      } else {
        setDeleteError(t(statusOf(error) === 403 ? 'API_KEYS_NO_ACCESS' : 'API_KEYS_DELETE_FAILED'));
      }
    } finally {
      setDeleteBusy(false);
    }
  };

  const copy = async () => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setCopyError(false);
    } catch {
      setCopyError(true);
    }
  };

  const formatDate = (value: string) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });
  };

  return (
    <Page aria-label={t('API_KEYS_TITLE')}>
      <Toolbar>
        <div>
          <Title>{t('API_KEYS_TITLE')}</Title>
          <Description>{t('API_KEYS_DESCRIPTION')}</Description>
        </div>
        <Actions>
          <Action type="button" disabled={accessState === 'loading' || loading}
            onClick={() => setAccessVersion((value) => value + 1)}>{t('REFRESH')}</Action>
          {canCreate && <Action type="button" $primary onClick={openCreate}>{t('API_KEYS_CREATE')}</Action>}
        </Actions>
      </Toolbar>

      {accessState === 'loading' ? <Description role="status">{t('LOADING')}…</Description>
        : accessState !== 'ready' ? (
          <Empty role="alert">
            <Title>{t(accessState === 'denied' ? 'API_KEYS_NO_ACCESS' : 'API_KEYS_ACCESS_FAILED')}</Title>
            <Action type="button" onClick={() => setAccessVersion((value) => value + 1)}>{t('RETRY')}</Action>
          </Empty>
        ) : !canList ? <Empty role="alert">{t('API_KEYS_NO_ACCESS')}</Empty> : (
          <>
            <Toolbar>
              <Filter>
                <input type="checkbox" checked={mine} onChange={(event) => { setMine(event.target.checked); setPage(1); }} />
                {t('API_KEYS_MINE')}
              </Filter>
              {!loading && !listError && <Description>{t('API_KEYS_COUNT', { count })}</Description>}
            </Toolbar>
            {loading ? <Empty role="status">{t('LOADING')}…</Empty>
              : listError ? (
                <Empty role="alert">
                  <ErrorText>{t(listError === 'denied' ? 'API_KEYS_NO_ACCESS' : 'API_KEYS_LOAD_FAILED')}</ErrorText>
                  <Action type="button" onClick={() => setListVersion((value) => value + 1)}>{t('RETRY')}</Action>
                </Empty>
              ) : items.length === 0 ? (
                <Empty>
                  <Title>{t('API_KEYS_EMPTY')}</Title>
                  <Description>{t(mine ? 'API_KEYS_EMPTY_MINE' : 'API_KEYS_EMPTY_DESCRIPTION')}</Description>
                </Empty>
              ) : (
                <TableScroll>
                  <Table aria-label={t('API_KEYS_TITLE')}>
                    <thead><tr>
                      <th scope="col">{t('NAME')}</th>
                      <th scope="col">{t('API_KEYS_PREFIX')}</th>
                      <th scope="col">{t('API_KEYS_OWNER')}</th>
                      <th scope="col">{t('CREATED_AT')}</th>
                      {canDelete && <th scope="col"><span className="sr-only">{t('API_KEYS_ACTIONS')}</span></th>}
                    </tr></thead>
                    <tbody>{items.map((item) => (
                      <tr key={item.id}>
                        <td>{item.name || t('API_KEYS_UNNAMED')}</td>
                        <td><Code>{item.token_prefix}…</Code></td>
                        <td>{item.staff_user_email || `#${item.staff_user_id}`}</td>
                        <td><time dateTime={item.created_at}>{formatDate(item.created_at)}</time></td>
                        {canDelete && <td><Action type="button" $danger
                          aria-label={t('API_KEYS_DELETE_LABEL', { name: item.name || item.token_prefix })}
                          onClick={() => { setDeleteError(''); setDeleting(item); }}>{t('API_KEYS_REVOKE')}</Action></td>}
                      </tr>
                    ))}</tbody>
                  </Table>
                </TableScroll>
              )}
            {!loading && !listError && count > PAGE_SIZE && (
              <Toolbar>
                <Description>{t('API_KEYS_PAGE', { page, pages: Math.ceil(count / PAGE_SIZE) })}</Description>
                <Actions>
                  <Action type="button" disabled={page === 1} onClick={() => setPage((value) => value - 1)}>{t('API_KEYS_PREVIOUS')}</Action>
                  <Action type="button" disabled={page >= Math.ceil(count / PAGE_SIZE)} onClick={() => setPage((value) => value + 1)}>{t('NEXT')}</Action>
                </Actions>
              </Toolbar>
            )}
          </>
        )}

      <Usage>
        <Description>{t('API_KEYS_USAGE')}</Description>
        <Code>Authorization: OpenAPIToken &lt;token&gt;</Code>
        <a href={`${config.apiUrl}api/docs/`} target="_blank" rel="noopener noreferrer">{t('API_KEYS_DOCS')} ↗</a>
      </Usage>

      {creating && (
        <TokenDialog title={t('API_KEYS_CREATE')} locked={createForm.isSubmitting} onClose={() => setCreating(false)}>
          <Description>{t('API_KEYS_CREATE_DESCRIPTION')}</Description>
          <form onSubmit={createForm.handleSubmit}>
            <DialogBody>
              <FieldLabel>
                {t('API_KEYS_NAME')}
                <Field name="name" autoComplete="off" maxLength={255} value={createForm.values.name}
                  placeholder={t('API_KEYS_NAME_PLACEHOLDER')} onChange={createForm.handleChange}
                  onBlur={createForm.handleBlur} disabled={createForm.isSubmitting}
                  aria-invalid={!!(createForm.touched.name && createForm.errors.name)} aria-describedby="api-key-name-error" />
              </FieldLabel>
              {createForm.touched.name && createForm.errors.name && <ErrorText id="api-key-name-error">{createForm.errors.name}</ErrorText>}
              {createError && <ErrorText role="alert">{createError}</ErrorText>}
              <Actions>
                <Action type="button" disabled={createForm.isSubmitting} onClick={() => setCreating(false)}>{t('CANCEL')}</Action>
                <Action type="submit" $primary disabled={createForm.isSubmitting || !canCreate}>
                  {t(createForm.isSubmitting ? 'LOADING' : 'API_KEYS_CREATE')}
                </Action>
              </Actions>
            </DialogBody>
          </form>
        </TokenDialog>
      )}

      {secret && (
        <TokenDialog title={t('API_KEYS_CREATED')} locked onClose={() => setSecret(null)}>
          <Description>{t('API_KEYS_SAVE_SECRET')}</Description>
          <FieldLabel>{t('API_KEYS_SECRET')}
            <Secret readOnly value={secret} spellCheck={false} onFocus={(event) => event.currentTarget.select()} />
          </FieldLabel>
          {copyError && <ErrorText role="alert">{t('API_KEYS_COPY_FAILED')}</ErrorText>}
          <Actions>
            <Action type="button" $primary onClick={copy}>{t(copied ? 'COPIED' : 'COPY')}</Action>
            <Action type="button" onClick={() => setSecret(null)}>{t('API_KEYS_SAVED')}</Action>
          </Actions>
        </TokenDialog>
      )}

      {deleting && (
        <TokenDialog title={t('API_KEYS_REVOKE')} locked={deleteBusy} onClose={() => setDeleting(null)}>
          <Description>{t('API_KEYS_DELETE_CONFIRM', { name: deleting.name || deleting.token_prefix })}</Description>
          <Code>{deleting.token_prefix}… · {deleting.staff_user_email}</Code>
          {deleteError && <ErrorText role="alert">{deleteError}</ErrorText>}
          <Actions>
            <Action type="button" disabled={deleteBusy} onClick={() => setDeleting(null)}>{t('CANCEL')}</Action>
            <Action type="button" $danger disabled={deleteBusy || !canDelete} onClick={remove}>
              {t(deleteBusy ? 'LOADING' : 'API_KEYS_REVOKE')}
            </Action>
          </Actions>
        </TokenDialog>
      )}
    </Page>
  );
};

export default ApiKeys;
